import "./agent-off-widget-preview.css"

import type { ReactNode } from "react"
import { memo } from "react"
import { IconArrowUpFill18 } from "nucleo-ui-fill-18"
import {
	IconAtom2Outline18,
	IconCopyOutline18,
	IconMicrophoneOutline18,
	IconPaperclip2Outline18,
	IconThumbsDownOutline18,
	IconThumbsUpOutline18,
	IconXmarkOutline18,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/browser"
import { AiThinkingOrb } from "@productlane/ui/src/components/ai-chat/thinking-orb"

// Sizes and colours are the widget's own (its root font is 15px), copied as px
// so the preview matches the embedded widget whatever the dashboard's root size.

const CustomerBubble = ({
	className,
	children,
}: {
	className: string
	children: ReactNode
}) => (
	<div
		className={cn(
			"ml-auto w-fit max-w-[75%] rounded-[5.6px] bg-gray-100 px-[9.4px] py-[5.6px] text-[14px] leading-normal text-gray-900 dark:bg-gray-800 dark:text-gray-100",
			className,
		)}
	>
		{children}
	</div>
)

const Thinking = ({ className }: { className: string }) => (
	<div
		className={cn(
			"flex h-6 items-center gap-2 text-gray-600 [grid-area:1/1] dark:text-gray-400",
			className,
		)}
	>
		<AiThinkingOrb className="flex-none" />
		<span className="ai-loading-shimmer text-[13.5px]">Thinking ...</span>
	</div>
)

const AgentName = () => (
	<div className="mb-[5px] flex items-center gap-2">
		<span className="flex size-5 flex-none items-center justify-center text-gray-900 dark:text-white">
			<IconAtom2Outline18 className="h-[17px] w-[17px] flex-none" />
		</span>
		<span className="text-[12.5px] font-semibold text-gray-900 dark:text-white">
			AI Agent
		</span>
		<span className="text-[12.5px] text-gray-500 dark:text-gray-400">
			Just now
		</span>
	</div>
)

const Line = ({ className, text }: { className: string; text: string }) => (
	<span className={cn("block w-fit whitespace-nowrap", className)}>{text}</span>
)

const ControlIcon = ({ Icon }: { Icon: typeof IconCopyOutline18 }) => (
	<span className="rounded-md p-1 text-gray-400 dark:text-gray-500">
		<Icon className="h-3.5 w-3.5 flex-none" />
	</span>
)

const ComposerIcon = ({ Icon }: { Icon: typeof IconCopyOutline18 }) => (
	<span className="flex h-[30px] w-[30px] items-center justify-center rounded-lg text-gray-600 dark:text-gray-400">
		<Icon className="h-4 w-4 flex-none" />
	</span>
)

export const AgentOffWidgetPreview = memo(() => (
	<div
		aria-hidden
		inert
		className="aowp-stage pointer-events-none relative flex w-full max-w-[720px] flex-none select-none justify-center py-12 max-md:hidden"
	>
		<div className="aowp-dots absolute inset-0" />
		<div className="relative flex h-[560px] w-[400px] flex-col overflow-hidden rounded-[26px] bg-white text-[13px] tracking-normal text-gray-900 shadow-xl ring-1 ring-black/[0.08] dark:bg-gray-900 dark:text-gray-100 dark:shadow-black/70 dark:ring-white/[0.09]">
			<div className="flex flex-none items-center justify-between px-[19px] py-[13px]">
				<div className="flex h-[26px] items-center gap-2 text-gray-900 dark:text-white">
					<IconAtom2Outline18 className="h-[18px] w-[18px] flex-none" />
					<span className="text-[15px] font-semibold">AI Agent</span>
				</div>
				<IconXmarkOutline18 className="h-4 w-4 flex-none text-gray-600 dark:text-gray-400" />
			</div>

			<div className="aowp-thread flex min-h-0 flex-1 flex-col gap-[11px] px-[19px] pt-[15px]">
				<CustomerBubble className="aowp-ask-1">
					How do I add a teammate to our workspace?
				</CustomerBubble>

				<div className="grid">
					<Thinking className="aowp-think-1" />
					<div className="aowp-answer-1 [grid-area:1/1]">
						<AgentName />
						<div className="pl-0.5 text-[14.25px] leading-normal tracking-[-0.01em]">
							<Line
								className="aowp-line-1"
								text="Go to Settings, Members and click Invite."
							/>
							<Line
								className="aowp-line-2"
								text="Invites stay valid for 7 days, and each new"
							/>
							<Line
								className="aowp-line-3"
								text="member adds a seat to your next invoice."
							/>
						</div>
						<div className="aowp-controls">
							<div className="-ml-0.5 flex items-center gap-1 pt-1">
								<ControlIcon Icon={IconThumbsUpOutline18} />
								<ControlIcon Icon={IconThumbsDownOutline18} />
								<ControlIcon Icon={IconCopyOutline18} />
							</div>
							<div className="ml-1 pb-1 pt-4 text-[12.5px] text-gray-600 dark:text-gray-400">
								3 Sources
							</div>
						</div>
					</div>
				</div>

				<CustomerBubble className="aowp-ask-2">Perfect, thanks!</CustomerBubble>

				<div className="grid">
					<Thinking className="aowp-think-2" />
					<div className="aowp-answer-2 [grid-area:1/1]">
						<AgentName />
						<div className="pl-0.5 text-[14.25px] leading-normal tracking-[-0.01em]">
							<Line
								className="aowp-line-4"
								text="Glad that helped, John. Anything else?"
							/>
						</div>
					</div>
				</div>
			</div>

			<div className="flex-none px-[11px] pb-[11px]">
				<div className="flex flex-col gap-[7.5px] rounded-[15px] border-[0.5px] border-black/10 bg-white px-[7.5px] pb-[7.5px] pt-[15px] dark:border-white/10 dark:bg-gray-900">
					<div className="grid px-[7.5px] text-[13px] font-medium tracking-[-0.01em]">
						<span className="aowp-placeholder text-gray-400 [grid-area:1/1] dark:text-gray-500">
							Ask anything
						</span>
						<span className="aowp-typed-1 w-fit whitespace-nowrap text-gray-900/90 [grid-area:1/1] dark:text-gray-50/90">
							How do I add a teammate to our workspace?
						</span>
						<span className="aowp-typed-2 w-fit whitespace-nowrap text-gray-900/90 [grid-area:1/1] dark:text-gray-50/90">
							Perfect, thanks!
						</span>
					</div>
					<div className="flex items-center justify-end gap-0.5">
						<ComposerIcon Icon={IconPaperclip2Outline18} />
						<ComposerIcon Icon={IconMicrophoneOutline18} />
						<span className="ml-0.5 grid h-[26px] w-[26px]">
							<span className="flex items-center justify-center rounded-full border border-black/15 bg-white text-gray-600 [grid-area:1/1] dark:border-white/15 dark:bg-gray-900 dark:text-gray-400">
								<IconArrowUpFill18 className="h-4 w-4 flex-none" />
							</span>
							<span className="aowp-send-on flex items-center justify-center rounded-full bg-gray-900 text-white [grid-area:1/1] dark:bg-white dark:text-gray-900">
								<IconArrowUpFill18 className="h-4 w-4 flex-none" />
							</span>
						</span>
					</div>
				</div>
			</div>
		</div>
	</div>
))
